import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";

import { useCallback, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import "react-popper-tooltip/dist/styles.css";

import ErrorBoundary from "@/components/errorBoundary";
import SignedInLayout from "@/components/signedInLayout";
import { DownloadProgressProvider } from "@/contexts/downloadProgress";
import UserContext from "@/contexts/user";
import useAuth from "@/hooks/useAuth";
import type { User } from "@/apiClient/models/User";
import type { MakoAPIClient } from "@/apiClient";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    noLayout?: boolean;
  }
}

export interface RouterContext {
  queryClient: QueryClient;
  api: MakoAPIClient;
}

const getCachedUser = (): User | null => {
  try {
    return JSON.parse(window.localStorage.getItem("makoUser") || "null");
  } catch {
    return null;
  }
};

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-blue-500">
      <div className="text-center px-6">
        <h1 className="text-8xl font-bold text-white tracking-tight">404</h1>
        <p className="mt-4 text-xl text-blue-50">Page Not Found</p>
        <Link
          to="/"
          className="mt-8 inline-block rounded-md bg-blue-100 px-6 py-3 text-sm font-medium text-white hover:bg-blue-200 transition-colors"
        >
          Return to Home
        </Link>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});

function AuthenticatedRoutes({ showLayout }: { showLayout: boolean }) {
  useAuth();

  return showLayout ? (
    <SignedInLayout>
      <Outlet />
    </SignedInLayout>
  ) : (
    <Outlet />
  );
}

function RootComponent() {
  const [user, setUser] = useState<User | null>(null);
  const [isUserLoading, setIsUserLoading] = useState(true);

  useEffect(() => {
    const cachedUser = getCachedUser();
    setUser(cachedUser);
    setIsUserLoading(false);
  }, []);

  const matches = useRouterState({ select: (s) => s.matches });
  const deepestMatch = matches[matches.length - 1];
  const { queryClient } = Route.useRouteContext();

  const setUserWithLocalStorage = useCallback((user: User | null) => {
    window.localStorage.setItem("makoUser", JSON.stringify(user));
    setUser(user);
  }, []);

  if (isUserLoading) {
    return null;
  }

  const showLayout = !!user && !deepestMatch?.staticData?.noLayout;

  return (
    <HelmetProvider>
      <Helmet>
        <title>OceanX Mako</title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Helmet>
      <DownloadProgressProvider>
        <UserContext.Provider
          value={{ user, isUserLoading, setUser: setUserWithLocalStorage }}
        >
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              <AuthenticatedRoutes showLayout={showLayout} />
            </ErrorBoundary>
          </QueryClientProvider>
        </UserContext.Provider>
        <ToastContainer />
      </DownloadProgressProvider>
    </HelmetProvider>
  );
}
